import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    inject,
    input,
    InputSignal,
    output,
    signal,
    Signal,
    viewChild,
    WritableSignal,
} from '@angular/core';

import { MatSelectModule } from '@angular/material/select';

import { IqEventBusNode } from '@dat/iq-event-bus';
import { IqBuilderLabelComponent } from '@dat/iq-style-processor';
import { debounce, get } from 'lodash';
import {
    IqBuilderAuditToolsService,
    TemplateDataEvent,
} from './iq-builder-audit-tools.service';

import '@alenaksu/json-viewer';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Dashboard } from '../models';
import { SortActionsPipe } from './sort-actions.pipe';
import {
    EventsTraceComponent,
    IqBuilderAuditToolDashboardEventsComponent,
} from './tools';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

export type RenderedListCardInfo = {
    name: string;
    id: string;
    label: string;
    additionalInfo?: {
        id: string;
        label: string;
        value: string;
    }[];
    hasError?: boolean;
};

type Filter = {
    actionName: string;
    query: string;
    hasError: boolean;
};

type RenderedActionInfo = {
    payload: any;
};

type PrerenderedInfo = {
    template: string;
    data: any;
    eventName: string;
};

type ErrorInfo = {
    error: string;
};

type Tables = 'All' | 'Event Map';

@Component({
    selector: 'iq-builder-audit-tools',
    standalone: true,
    imports: [
        CommonModule,
        IqBuilderLabelComponent,
        ReactiveFormsModule,
        MatSelectModule,
        IqBuilderAuditToolDashboardEventsComponent,
        MatFormFieldModule,
        MatCheckboxModule,
        EventsTraceComponent,
        MatInputModule,
        SortActionsPipe,
        CdkDrag,
        CdkDragHandle,
    ],
    templateUrl: './iq-builder-audit-tools.component.html',
    styleUrl: './iq-builder-audit-tools.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class IqBuilderAuditToolsComponent {
    private _service = inject(IqBuilderAuditToolsService);

    private _fb = inject(FormBuilder);
    fg = this._fb.group<Filter>({
        actionName: '',
        query: '',
        hasError: false,
    });
    closed = output();

    tableTypes: Tables[] = ['All', 'Event Map'];

    isFullScreen: WritableSignal<boolean> = signal(true);

    currentTable: WritableSignal<Tables> = signal('All');
    selectedRenderedAction: WritableSignal<string> = signal('');
    dashboardInfo: InputSignal<Dashboard> = input({
        id: '',
        sections: [],
    });
    currentRenderedError: WritableSignal<any> = signal(null);
    filterActionsError: WritableSignal<any> = signal(null);
    payload: WritableSignal<any> = signal(null);
    template: WritableSignal<TemplateDataEvent> = signal(null);
    traceId: WritableSignal<string> = signal(null);

    cardDebounced = debounce(
        (renderedActions, filter) => {
            const cards = Object.values(renderedActions).map(
                (value: Partial<IqEventBusNode>): RenderedListCardInfo => {
                    const additionalInfo = [
                        'evt.payload.controlId',
                        'evt.payload.dashboardControlId',
                        'evt.payload.control.id',
                        'evt.payload.controlName',
                        'evt.payload.type',
                        'evt.payload.query.id',
                        'evt.payload.query.type',
                        'evt.payload.id',
                    ]
                        .map((path) => {
                            const labelValue: string = get(value, path);
                            const pathLabel: string = {
                                'evt.payload.dashboardControlId': 'Control ID',
                                'evt.payload.controlId': 'Control ID',
                                'evt.payload.control.id': 'Control ID',
                                'evt.payload.controlName': 'Control Name',
                                'evt.payload.type': 'Type',
                                'evt.payload.query.id': 'Query ID',
                                'evt.payload.query.type': 'Query Type',
                                'evt.payload.id': 'ID',
                            }[path];

                            if (!labelValue?.length) return null;

                            return {
                                id: Math.random().toString(),
                                label: pathLabel,
                                value: labelValue,
                            };
                        })
                        .filter((value) => value);
                    const hasError =
                        !!this._service.currentRenderedActionError(
                            value.id,
                        )() ||
                        !!this._service.currentFilterActionsError(value.id)();

                    return {
                        name: value?.evt?.name,
                        id: value.id,
                        label: `<h3 class="headline-small">${value?.evt?.name}</h3>`,
                        additionalInfo,
                        hasError,
                    };
                },
            );

            return cards.filter((card) => {
                if (
                    !filter?.actionName?.length &&
                    !filter?.query?.length &&
                    !filter?.hasError
                )
                    return true;

                const actionNameMatch = !!filter?.actionName?.length
                    ? card.name === filter.actionName
                    : false;
                const queryNameMatch = !!filter?.query?.length
                    ? card.name
                          .toLowerCase()
                          .includes(filter.query.toLowerCase())
                    : false;
                const errorMatch = filter?.hasError ? card.hasError : false;

                return actionNameMatch || queryNameMatch || errorMatch;
            });
        },
        300,
        {
            maxWait: 500,
        },
    );

    payloadEl: Signal<any> = viewChild('payloadEl');
    templateDataEl: Signal<any> = viewChild('templateDataEl');
    private _fgSignal: Signal<Partial<Filter>>;

    showTemplate = signal(false);
    showTemplateData = signal(true);

    constructor() {
        // effect(() => {
        //     console.log(this._fgSignal());
        // });

        // effect(() => {
        //     console.log(this._service.state());
        // });

        this._fgSignal = toSignal(this.fg.valueChanges);
    }

    renderActionOptions: Signal<{ value: string; label: string }[]> = computed(
        () => {
            const renderedActions: Record<
                string,
                Partial<IqEventBusNode>
            > = this._service.renderedActions();
            const values = Object.values(renderedActions).map(
                (value: Partial<IqEventBusNode>): string => value.evt.name,
            );
            const uniqueValues = [...new Set(values)];
            const options = uniqueValues.map(
                (value: string): { value: string; label: string } => {
                    return {
                        value: value,
                        label: value,
                    };
                },
            );

            return options;
        },
    );

    renderedActionListCards: Signal<RenderedListCardInfo[]> = computed(() => {
        const renderedActions: Record<
            string,
            Partial<IqEventBusNode>
        > = this._service.renderedActions();
        const filter = this._fgSignal();

        return this.cardDebounced(renderedActions, filter);
    });

    renderedActionInfo: Signal<RenderedActionInfo> = computed(() => {
        const payload = this.payload();

        if (!payload) return { payload: {} };

        return {
            payload,
        };
    });

    prerenderedActionInfo: Signal<PrerenderedInfo> = computed(() => {
        const selectedAction = this.template();

        if (!selectedAction) return null;

        return {
            template: selectedAction.template,
            data: selectedAction.data,
            eventName: selectedAction.eventName,
        };
    });

    renderedActionErrorInfo: Signal<ErrorInfo> = computed(() => {
        const selectedAction = this.currentRenderedError();

        if (!selectedAction) return null;

        return {
            error: selectedAction.error,
        };
    });

    filterActionsErrorInfo: Signal<ErrorInfo> = computed(() => {
        const selectedAction = this.filterActionsError();

        if (!selectedAction) return null;

        return {
            error: selectedAction.error,
        };
    });

    selectActions(table: Tables) {
        this.currentTable.set(table);
    }

    clickClose() {
        this.closed.emit();
    }

    toggleFullScreen() {
        const isFullScreen = this.isFullScreen();

        this.isFullScreen.set(!isFullScreen);
    }

    selectAllAction(action: RenderedListCardInfo) {
        const selectedAction = this._service.renderedActions()?.[action.id];
        const templateAction = this._service.prerenderedAction(action.id)?.();
        const renderError = this._service.currentRenderedActionError(
            action.id,
        )?.();
        const filterError = this._service.currentFilterActionsError(
            action.id,
        )?.();
        const traceId = this._service.currentEventTrace(action.id)()?.id;

        this.selectedRenderedAction.set(action.id);
        this.currentRenderedError.set(renderError);
        this.payload.set(selectedAction.evt.payload);
        this.filterActionsError.set(filterError);
        this.template.set(templateAction);
        this.traceId.set(traceId);
    }

    toggleTemplate() {
        const showTemplate = this.showTemplate();

        this.showTemplate.set(!showTemplate);
    }

    toggleTemplateData() {
        const showTemplateData = this.showTemplateData();

        this.showTemplateData.set(!showTemplateData);
    }
}
